import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import {
  buildRetryAutoCurationInput,
  type DiscernRowData,
} from '@utils/discern-transforms';

const messages = defineMessages({
  retryDocument: {
    id: 'discernSummary.retryDocument',
    defaultMessage: 'Retry',
  },
  retryDisable: {
    id: 'discernSummary.retryDisable',
    defaultMessage: 'Can only retry a failed or stalled document',
  },
  retryMissingPageGroupsTooltip: {
    id: 'discernSummary.retryMissingPageGroupsTooltip',
    defaultMessage:
      'Page groups are missing. Re-upload this document to curate it.',
  },
  retryInProgress: {
    id: 'discernSummary.retryInProgress',
    defaultMessage: 'Retry in progress',
  },
});

interface RetryAutoCurationButtonProps {
  rowData: DiscernRowData;
  handleRetryDocument: (rowData: DiscernRowData) => Promise<void>;
  /** True while this row's retry mutation is in flight. */
  retrying?: boolean;
}

const RetryAutoCurationButton: React.FC<RetryAutoCurationButtonProps> = ({
  rowData,
  handleRetryDocument,
  retrying = false,
}) => {
  const { formatMessage } = useIntl();

  // Two things have to agree. The server owns retryability (can_retry) because
  // a stalled Structuring job also qualifies and that depends on a stale
  // window the client can't see. But can_retry says nothing about page groups,
  // and a job that failed before grouping ran has none to resend, so the
  // mutation input can't be built. Check both or the button offers a retry
  // that dies on click.
  const missingInput = buildRetryAutoCurationInput(rowData) === null;
  const canRetry = rowData.canRetry && !missingInput;

  function tooltip() {
    if (retrying) return messages.retryInProgress;
    if (!rowData.canRetry) return messages.retryDisable;
    if (missingInput) return messages.retryMissingPageGroupsTooltip;
    return messages.retryDocument;
  }

  const retryButton = (
    <IconButton
      data-testid="retry-auto-curation-button"
      disabled={!canRetry || retrying}
      onClick={(e) => {
        e.stopPropagation();
        handleRetryDocument(rowData);
      }}
      sx={{
        opacity: canRetry && !retrying ? 1 : 0.5,
        cursor: canRetry && !retrying ? 'pointer' : 'not-allowed',
      }}
    >
      <Icon name="arrow-rotate-right" size="sm" type="fas" />
    </IconButton>
  );

  return (
    <Tooltip title={formatMessage(tooltip())}>
      <span
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {retryButton}
      </span>
    </Tooltip>
  );
};

export default RetryAutoCurationButton;
